import { Dialog, DialogPanel, Transition, TransitionChild, DialogTitle } from '@headlessui/react';
import { XCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/solid';
import { Fragment, useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { generatePath, useHistory } from 'react-router';
import { useActionModal } from '../../hooks/ActionModal.hook';
import { useCurrentConfiguration } from '../../hooks/CurrentConfiguration.hook';
import { CreateConfigurationMutationVariables, useCreateConfigurationMutation } from '../../lib/graphql.generated';

type FormValues = {
  name: string;
};

function SavePanelModal() {
  const { t } = useTranslation();
  const { savePanel } = useActionModal();
  const {
    register, handleSubmit, formState: {
      errors: formErrors,
      isValid,
    },
  } = useForm<FormValues>({
    mode: 'all',
  });
  const { configuration, isSaveable, reset, generatePanelName } = useCurrentConfiguration();
  const [createConfigurationMutation, {
    data: saveData,
    error: saveError,
  }] = useCreateConfigurationMutation();
  const history = useHistory();

  const onSubmit: SubmitHandler<FormValues> = (formData) => {
    if (!configuration) {
      return;
    }
    const newConfig: CreateConfigurationMutationVariables = {
      name: formData.name,
      width: configuration.width,
      customWidth: configuration.customWidth,
      plugRegion: configuration.plugRegion,
      components: configuration.components.map((i) => i.id),
    };
    void createConfigurationMutation({ variables: newConfig });
  };

  useEffect(() => {
    if (saveError) {
      console.error('SAVE ERROR:', saveError);
    } else if (saveData) {
      reset(configuration?.plugRegion!, configuration?.width!, 0);
      savePanel.close();
      history.push(generatePath('/panels/:id', { id: saveData.createConfiguration.id }));
    }
  }, [saveData, saveError, configuration, history, reset, savePanel]);

  return (
    <Transition show={savePanel.isOpen} as={Fragment}>
      <Dialog
        id="save panel"
        open={savePanel.isOpen}
        onClose={savePanel.close}
        className="fixed inset-0 z-10 overflow-y-auto"
      >
        <div className="min-h-screen px-4 text-center">
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </TransitionChild>
          <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <DialogPanel className="inline-block align-bottom bg-white mt-auto mb-auto rounded-lg text-left sm:rounded-md overflow-hidden shadow-xl transform transition-all sm:align-middle sm:max-w-lg sm:w-full">
              <DialogTitle className="flex flex-row justify-between top-0 right-0 py-4 px-4 border-b border-gray-200 w-full">
                <span className="text-lg leading-6 font-medium text-gray-900">{t('panel.modal.savePanelHeader')}</span>
                <button onClick={savePanel.close} type="button" className="bg-white rounded-md text-gray-400 hover:text-red-500 focus:outline-none focus:ring-0 focus:ring-offset-2 focus:ring-gray-400">
                  <span className="sr-only">{t('modal.closeButton')}</span>
                  <XCircleIcon className="h-6 w-6" />
                </button>
              </DialogTitle>
              <div className="px-4 pt-5 pb-4 sm:p-6">
                <div className="mt-5 md:mt-0 md:col-span-2">
                  <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="grid grid-cols-9 gap-2">
                      <label htmlFor="name" className="col-span-1 self-center text-sm font-medium text-gray-700">{t('label.name')}:</label>
                      <div className="col-span-8 relative rounded-md">
                        <input
                          type="text"
                          id="name"
                          {...register('name', { required: true })}
                          disabled={!isSaveable()}
                          readOnly={true}
                          value={generatePanelName()}
                          className={'p-0 block w-full sm:text-sm rounded-md border-none cursor-default focus:ring-transparent'}
                        />
                        {formErrors.name ? (
                          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none border-primary-red-default">
                            <ExclamationCircleIcon className="h-5 w-5 text-red-500" />
                          </div>
                        ) : null}
                      </div>
                      {formErrors.name && <p className="mt-2 text-sm text-red-600" id="name-error">{t('panel.modal.savePanelErrorInputName')}</p>}
                    </div>
                    <div className="mt-4 py-2 text-right space-x-1">
                      <button onClick={savePanel.close} type="button" className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-primary-gray-default bg-gray-200 hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400">
                        {t('button.cancel')}
                      </button>
                      <button
                        type="submit"
                        disabled={!isSaveable() || !isValid}
                        className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-secondary-orange-default hover:bg-secondary-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 disabled:cursor-not-allowed disabled:bg-gray-300"
                      >
                        {t('navbar.button.save')}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition>
  );
}

export default SavePanelModal;